/**
 * Service Conducteurs - API Tracking
 * GET/POST /api/v1/drivers/ , GET/PUT/PATCH/DELETE /api/v1/drivers/{id}/
 */
import { createTrackingClient } from "../trackingApi";

export interface TrackingDriver {
  id: string;
  user_id: number | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  license_number: string | null;
  is_active: boolean;
  institution_id: number | null;
  institution_name: string | null;
  sub_area_id: number | null;
  sub_area_name: string | null;
}

export interface DriverCreatePayload {
  user_id: number;
  license_number?: string;
  is_active?: boolean;
  institution_id?: number | null;
  sub_area_id?: number | null;
}

export interface DriverUpdatePayload {
  license_number?: string;
  is_active?: boolean;
  institution_id?: number | null;
  sub_area_id?: number | null;
}

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

export async function fetchTrackingDrivers(
  accessToken: string | undefined
): Promise<TrackingDriver[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<TrackingDriver[] | { results: TrackingDriver[] }>("/api/v1/drivers/");
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: TrackingDriver[] }).results;
  return [];
}

export async function createTrackingDriver(
  accessToken: string | undefined,
  payload: DriverCreatePayload
): Promise<TrackingDriver> {
  const client = getClient(accessToken);
  const { data } = await client.post<TrackingDriver>("/api/v1/drivers/", payload);
  return data;
}

export async function updateTrackingDriver(
  accessToken: string | undefined,
  id: string,
  payload: DriverUpdatePayload
): Promise<TrackingDriver> {
  const client = getClient(accessToken);
  const { data } = await client.put<TrackingDriver>(`/api/v1/drivers/${id}/`, payload);
  return data;
}

export async function patchTrackingDriver(
  accessToken: string | undefined,
  id: string,
  payload: Partial<DriverUpdatePayload>
): Promise<TrackingDriver> {
  const client = getClient(accessToken);
  const { data } = await client.patch<TrackingDriver>(`/api/v1/drivers/${id}/`, payload);
  return data;
}

export async function deleteTrackingDriver(
  accessToken: string | undefined,
  id: string
): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/drivers/${id}/`);
}
